import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  GlobalStyles,
  Typography,
  TextField,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import useSparks from '../hooks/useSparks';
import { useSquircle } from '../hooks/useSquircle';
import { getWordPatterns } from '../utils/nlpUtils';

const CARD_BG = '#fffeed';
const DARK_BG = '#1a1a2e';
const MUTED = '#c3c3d4';

const ROBOTO_FLEX_WIDE =
  "'GRAD' 0, 'XOPQ' 96, 'XTRA' 468, 'YOPQ' 79, 'YTAS' 750, 'YTDE' -203, 'YTFI' 738, 'YTLC' 514, 'YTUC' 712, 'wdth' 151";
const ROBOTO_FLEX_NORMAL =
  "'GRAD' 0, 'XOPQ' 96, 'XTRA' 468, 'YOPQ' 79, 'YTAS' 750, 'YTDE' -203, 'YTFI' 738, 'YTLC' 514, 'YTUC' 712, 'wdth' 100";
const ROBOTO_SERIF_SETTINGS = "'GRAD' 0, 'wdth' 100";

const SPARK_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];
const BURST_DURATION = 300;

function WordSparkBurst({ x, y, rotation = 0, delay = 0 }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        left: x,
        top: y,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'visible',
        transform: `rotate(${rotation}deg)`,
      }}
    >
      {SPARK_ANGLES.map((angle, i) => (
        <Box key={angle} sx={{
          position: 'absolute',
          width: 0,
          height: 0,
          left: 0,
          top: 0,
          transform: `rotate(${angle}deg)`,
          transformOrigin: '0 0',
        }}>
          <Box sx={{
            position: 'absolute',
            width: '3px',
            height: '18px',
            bgcolor: '#FF371D',
            borderRadius: '2px',
            left: '-1.5px',
            top: '-18px',
            opacity: 0,
            animation: `wordSparkOut ${BURST_DURATION}ms ${delay + i * 12}ms ease-out forwards`,
          }} />
        </Box>
      ))}
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: '"Roboto Flex", sans-serif',
        fontWeight: 900,
        fontSize: '16px',
        textTransform: 'uppercase',
        color: CARD_BG,
        fontVariationSettings: ROBOTO_FLEX_WIDE,
        letterSpacing: '0.02em',
      }}
    >
      {children}
    </Typography>
  );
}

function WordRow({ word, count }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        width: '100%',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Roboto Flex", sans-serif',
          fontWeight: 700,
          fontSize: '48px',
          lineHeight: '48px',
          color: CARD_BG,
          textTransform: 'uppercase',
          fontVariationSettings: ROBOTO_FLEX_NORMAL,
          whiteSpace: 'nowrap',
        }}
      >
        {word}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Roboto Flex", sans-serif',
          fontWeight: 700,
          fontSize: '48px',
          lineHeight: '48px',
          color: CARD_BG,
          textTransform: 'uppercase',
          fontVariationSettings: ROBOTO_FLEX_NORMAL,
          whiteSpace: 'nowrap',
        }}
      >
        {count}
      </Typography>
    </Box>
  );
}

function WordSection({ label, words }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
      <SectionLabel>{label}</SectionLabel>
      {words.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {words.map((item) => (
            <WordRow key={item.word} word={item.word} count={item.count} />
          ))}
        </Box>
      ) : (
        <Typography
          sx={{
            color: MUTED,
            fontSize: '15px',
            fontFamily: '"Roboto Flex", sans-serif',
            fontVariationSettings: ROBOTO_FLEX_NORMAL,
            py: 1,
          }}
        >
          No data yet
        </Typography>
      )}
    </Box>
  );
}

function InsightsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sparks, addSpark } = useSparks();
  const [patternInput, setPatternInput] = useState('');
  const [segment, setSegment] = useState('overall');
  const verbRef = useRef(null);
  const subjectRef = useRef(null);
  const [bursts, setBursts] = useState([]);
  const burstKeyRef = useRef(0);
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname === '/patterns' && prevPathRef.current !== '/patterns') {
      const k = ++burstKeyRef.current;
      let clearTimer;
      // Wait for slide transition to complete before measuring positions
      const measureTimer = setTimeout(() => {
        const positions = [];
        if (verbRef.current) {
          const r = verbRef.current.getBoundingClientRect();
          positions.push({ id: `vtl-${k}`, x: r.left,  y: r.top,    rotation: -15, delay: 0   });
          positions.push({ id: `vtr-${k}`, x: r.right, y: r.top,    rotation:  15, delay: 80  });
        }
        if (subjectRef.current) {
          const r = subjectRef.current.getBoundingClientRect();
          positions.push({ id: `sbl-${k}`, x: r.left,  y: r.bottom, rotation: -10, delay: 160 });
          positions.push({ id: `sbr-${k}`, x: r.right, y: r.bottom, rotation:  10, delay: 240 });
        }
        setBursts(positions);
        clearTimer = setTimeout(() => setBursts([]), 600);
      }, 420);
      return () => {
        clearTimeout(measureTimer);
        clearTimeout(clearTimer);
      };
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  const { ref: createCardRef, squircleStyle: createCardSquircle } = useSquircle({
    cornerRadius: 36,
    cornerSmoothing: 1,
  });

  const ledToAction = segment === 'action';
  const { verbs, subjects } = getWordPatterns(sparks, { ledToAction });

  const topVerb = verbs[0]?.word ?? null;
  const topSubject = subjects[0]?.word ?? null;
  const hasData = topVerb || topSubject;

  const handleCreateSpark = useCallback(() => {
    if (!patternInput.trim()) return;
    const newSpark = addSpark({
      moment: patternInput.trim(),
      why: null,
      intensity: 2,
      tellingYou: null,
      compelledTo: null,
      timeframe: null,
      completed: false,
    });
    setPatternInput('');
    navigate(`/edit/${newSpark.id}`);
  }, [patternInput, addSpark, navigate]);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      <GlobalStyles styles={{
        '@keyframes wordSparkOut': {
          '0%':   { opacity: 1, transform: 'translateY(0px) scale(1)' },
          '60%':  { opacity: 1 },
          '100%': { opacity: 0, transform: 'translateY(-28px) scale(0.2)' },
        },
      }} />
      {bursts.length > 0 && createPortal(
        bursts.map((b) => (
          <WordSparkBurst key={b.id} x={b.x} y={b.y} rotation={b.rotation} delay={b.delay} />
        )),
        document.body
      )}
      <Container maxWidth="sm" disableGutters>
        <Box sx={{ px: 2, pt: 1, pb: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Hero — top verb & subject, no card */}
          <Box sx={{ textAlign: 'center' }}>
            <SectionLabel>Your top verb &amp; subject</SectionLabel>
            {hasData ? (
              <Box sx={{ textAlign: 'center', mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                {topVerb && (
                  <Box ref={verbRef} sx={{ display: 'inline-flex', position: 'relative', px: '14px', py: '6px' }}>
                    <Box sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: '#FF371D',
                      clipPath: 'polygon(2% 8%, 98% 2%, 96% 94%, 4% 98%)',
                    }} />
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: '"Roboto Serif", serif',
                        fontWeight: 900,
                        fontSize: '48px',
                        lineHeight: 1.1,
                        color: CARD_BG,
                        textTransform: 'uppercase',
                        fontVariationSettings: ROBOTO_SERIF_SETTINGS,
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      &ldquo;{topVerb}&rdquo;
                    </Typography>
                  </Box>
                )}
                {topSubject && (
                  <Box ref={subjectRef} sx={{ display: 'inline-flex', position: 'relative', px: '14px', py: '6px' }}>
                    <Box sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: '#FF371D',
                      clipPath: 'polygon(3% 5%, 97% 3%, 98% 92%, 1% 96%)',
                    }} />
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: '"Roboto Serif", serif',
                        fontWeight: 900,
                        fontSize: '48px',
                        lineHeight: 1.1,
                        color: CARD_BG,
                        textTransform: 'uppercase',
                        fontVariationSettings: ROBOTO_SERIF_SETTINGS,
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      &ldquo;{topSubject}&rdquo;
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography
                sx={{
                  color: MUTED,
                  fontSize: '15px',
                  fontFamily: '"Roboto Flex", sans-serif',
                  fontVariationSettings: ROBOTO_FLEX_NORMAL,
                  mt: 0.5,
                }}
              >
                Add sparks to see your top patterns
              </Typography>
            )}
          </Box>

          {/* Create spark card */}
          <Box
            ref={createCardRef}
            style={createCardSquircle}
            sx={{
              bgcolor: CARD_BG,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              placeholder="Does this pattern spark a new idea?"
              value={patternInput}
              onChange={(e) => setPatternInput(e.target.value)}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: '1.125rem',
                  lineHeight: 1.6,
                  p: 0,
                  fontFamily: '"Roboto Serif", serif',
                  fontVariationSettings: ROBOTO_SERIF_SETTINGS,
                  color: DARK_BG,
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  bgcolor: 'transparent',
                  p: 0,
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(26,26,46,0.45)',
                  opacity: 1,
                },
              }}
            />

            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleCreateSpark}
              disabled={!patternInput.trim()}
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '100px',
                bgcolor: patternInput.trim() ? '#ff371d' : 'rgba(26,26,46,0.1)',
                color: patternInput.trim() ? CARD_BG : 'rgba(26,26,46,0.38)',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: patternInput.trim() ? '#cc2a12' : 'rgba(26,26,46,0.1)',
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(26,26,46,0.1)',
                  color: 'rgba(26,26,46,0.38)',
                },
              }}
            >
              Create spark
            </Button>
          </Box>

          {/* Your top inspiration section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>

            <SectionLabel>Your top inspiration</SectionLabel>

            {/* Segmented button: Overall / Led to action */}
            <Box
              sx={{
                display: 'flex',
                border: `1px solid ${CARD_BG}`,
                borderRadius: '100px',
                overflow: 'hidden',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {[
                { key: 'overall', label: 'Overall' },
                { key: 'action', label: 'Led to action' },
              ].map((option) => {
                const isSelected = segment === option.key;
                return (
                  <Box
                    key={option.key}
                    onClick={() => setSegment(option.key)}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.75,
                      px: 2.5,
                      py: 1,
                      cursor: 'pointer',
                      bgcolor: isSelected ? CARD_BG : 'transparent',
                      color: isSelected ? DARK_BG : CARD_BG,
                      transition: 'all 0.2s',
                      userSelect: 'none',
                    }}
                  >
                    {isSelected && (
                      <CheckIcon sx={{ fontSize: '16px', color: DARK_BG }} />
                    )}
                    <Typography
                      sx={{
                        fontFamily: '"Roboto Flex", sans-serif',
                        fontWeight: isSelected ? 600 : 400,
                        fontSize: '14px',
                        color: 'inherit',
                        fontVariationSettings: isSelected ? ROBOTO_FLEX_WIDE : ROBOTO_FLEX_NORMAL,
                        transition: 'all 0.2s',
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Verbs section */}
            <WordSection label="Verbs" words={verbs} />

            {/* Subjects section */}
            <WordSection label="Subjects" words={subjects} />

          </Box>

        </Box>
      </Container>
    </Box>
  );
}

export default InsightsScreen;
